import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import CamperInfoSection from "../components/CamperInfoSection";
import ParentInfoSection, {
  PaymentSection,
} from "../components/ParentInfoSection";
import axios from "axios";
import ReigstrationAgreement from "../components/RegistrationAgreement";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import "../styles/home.css";
import "../styles/registration.css";

const FIRST_CAMPER_FEE = 280;
const ADDITIONAL_CAMPER_FEE = 250;
const FALLBACK_FORM =
  "https://docs.google.com/forms/d/e/1FAIpQLSdoFa0NkSIySO6KEttZnlFltodF5ZgkwKMwOCk-BORzgb1aWA/viewform";

export default function RegistrationPage() {
  const methods = useForm({ shouldUnregister: true });
  const [loading, setIsLoading] = useState(false);
  const [numberOfSections, setNumberOfSections] = useState(1);
  const [didError, setDidError] = useState(false);
  const [hasInvalidFields, setHasInvalidFields] = useState(false);
  const navigate = useNavigate();
  const showToast = () =>
    toast.success(
      "You're registered. You'll receive a confirmation email with payment instructions within 36 hours.",
      { duration: 10000 }
    );

  const totalFee =
    FIRST_CAMPER_FEE + ADDITIONAL_CAMPER_FEE * (numberOfSections - 1);

  const onSubmit = (d) => {
    setHasInvalidFields(false);
    setIsLoading(true);
    var data = {
      guardianInformation: d.guardianInformation,
      campersInformation: d.campersInformation,
      agreement: d.agreement,
    };
    data.campersInformation.forEach((ele, index) => {
      ele.birthDate = `${ele.month}/${ele.day}/${ele.year}`;
    });
    axios
      .post(`https://api.houstonsikhyouthcamp.org/submit`, { data })
      .then((res) => {
        if (res.data.status === 200) {
          setIsLoading(false);
          showToast();
          navigate("/");
        }
      })
      .catch((e) => {
        document.body.scrollTop = document.documentElement.scrollTop = 0;
        setDidError(true);
        setIsLoading(false);
      });
  };

  return (
    <div className="hsyc reg">
      <nav className="hsyc-nav" aria-label="Main">
        <div className="hsyc-wrap hsyc-nav__bar">
          <a href="/" className="hsyc-nav__logo">
            <img src="/camplogo.png" alt="Houston Sikh Youth Camp home" />
          </a>
          <a href="/#winter-camp" className="reg-back">
            Camp details
          </a>
        </div>
      </nav>

      <header className="reg-intro">
        <div className="hsyc-wrap">
          <h1>Register for winter camp 2026</h1>
          <p>
            December 20 to 26 at YMCA Camp Cullen in Trinity, Texas. Register
            all your children on one form.
          </p>
        </div>
      </header>

      {didError && (
        <div className="hsyc-wrap">
          <div className="reg-alert" role="alert">
            <p>
              <strong>Your registration didn't go through.</strong> Nothing was
              saved. Try again, or{" "}
              <a href={FALLBACK_FORM} target="_blank" rel="noreferrer">
                register with our backup Google Form
              </a>
              .
            </p>
          </div>
        </div>
      )}

      <div className="hsyc-wrap reg-layout">
        <FormProvider {...methods}>
          <form
            className="reg-form"
            noValidate
            onSubmit={methods.handleSubmit(onSubmit, () =>
              setHasInvalidFields(true)
            )}
          >
            <section className="reg-step" aria-labelledby="step-campers">
              <h2 id="step-campers">
                <span className="reg-step__num">1</span>Campers
              </h2>
              {[...Array(numberOfSections)].map((num, idx) => (
                <CamperInfoSection
                  key={idx}
                  section_number={idx}
                  onRemove={
                    idx > 0 && idx === numberOfSections - 1
                      ? () => setNumberOfSections(numberOfSections - 1)
                      : undefined
                  }
                />
              ))}
              <button
                type="button"
                className="reg-add"
                onClick={() => setNumberOfSections(numberOfSections + 1)}
              >
                Add another camper
              </button>
            </section>

            <section className="reg-step" aria-labelledby="step-parents">
              <h2 id="step-parents">
                <span className="reg-step__num">2</span>Parents
              </h2>
              <ParentInfoSection />
            </section>

            <section className="reg-step" aria-labelledby="step-payment">
              <h2 id="step-payment">
                <span className="reg-step__num">3</span>Payment
              </h2>
              <p className="reg-step__lead">
                You'll pay after registering. Choose how, and we'll email the
                details.
              </p>
              <PaymentSection />
            </section>

            <section className="reg-step" aria-labelledby="step-policies">
              <h2 id="step-policies">
                <span className="reg-step__num">4</span>Camp policies
              </h2>
              <p className="reg-step__lead">
                Read each policy and check the box to agree.
              </p>
              <ReigstrationAgreement />
            </section>

            <div className="reg-submit">
              {hasInvalidFields && (
                <p className="reg-error" role="alert">
                  Some fields above need attention. We've marked each one.
                </p>
              )}
              <button
                type="submit"
                className="hsyc-button"
                disabled={loading}
              >
                {loading ? "Submitting registration…" : "Submit registration"}
              </button>
            </div>
          </form>
        </FormProvider>

        <aside className="reg-summary" aria-label="Registration summary">
          <h2>Your registration</h2>
          <dl>
            <div>
              <dt>Campers</dt>
              <dd>{numberOfSections}</dd>
            </div>
            <div>
              <dt>Camp fee</dt>
              <dd>${totalFee}</dd>
            </div>
          </dl>
          <p>
            ${FIRST_CAMPER_FEE} for the first child, ${ADDITIONAL_CAMPER_FEE}{" "}
            for each additional child.
          </p>
          <p>
            Once you submit, we'll email you a confirmation with payment
            instructions within 36 hours.
          </p>
        </aside>
      </div>
      <Footer />
    </div>
  );
}
